import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MessageSquare } from "lucide-react";
import {
  createAssistantThread,
  getAssistantMemory,
  getAssistantThread,
  listAssistantThreads,
  putAssistantMemory,
  resolveEntityThread,
  resolveCommandItemThread,
  sendAssistantMessage,
  updateAssistantThread,
} from "@/api/assistant";
import { getJob } from "@/api/jobs";
import { getCompany } from "@/api/companies";
import { getPipelineDetail } from "@/api/pipeline";
import { updateProfile } from "@/api/profile";
import { runEvaluation } from "@/api/run";
import { AssistantRail } from "@/components/assistant/assistant-rail";
import { AssistantEmptyState } from "@/components/assistant/assistant-empty-state";
import { AssistantComposer } from "@/components/assistant/assistant-composer";
import { AssistantThreadView } from "@/components/assistant/assistant-thread";
import { MobileSheet } from "@/components/mobile-sheet";
import {
  CONFIRM_ACTIONS,
  LINK_ACTIONS,
  actionLabel,
  parseActionEnvelopes,
  stripActionEnvelopes,
  type AssistantAction,
} from "@/lib/assistant-actions";
import type {
  AssistantEntityType,
  AssistantMessage,
  StreamEvent,
} from "@/lib/assistant-ui-types";

const ENTITY_STARTERS: Record<AssistantEntityType, readonly string[]> = {
  job: [
    "Explain fit for this role",
    "Draft an outreach note",
    "What gaps should I close?",
    "Prep interview questions",
  ],
  company: [
    "Research this company",
    "What should I know before applying?",
    "How do I stand out here?",
  ],
  pipeline: [
    "What's my next step?",
    "Prep for this stage",
    "Draft a follow-up",
  ],
  application: [
    "Explain this score",
    "What should I improve?",
    "Next steps for this application",
  ],
  career_move: [
    "Help me execute this move",
    "Draft the outreach / reply",
    "What am I missing?",
    "Is this the right priority today?",
  ],
  goal: [
    "Help me refine this goal",
    "Suggest work to move this forward",
    "What should I review this week?",
  ],
  plan: [
    "Refine this plan",
    "Suggest next work under this plan",
    "Is this plan still aligned?",
  ],
  work: [
    "How should I execute this?",
    "Break this into a simple approach",
    "What does done look like?",
  ],
};

function entityFromParams(sp: URLSearchParams): {
  entityType: AssistantEntityType;
  entityId: string;
  stage?: string;
} | null {
  // Career Moves deep-link takes priority.
  if (sp.get("careerMoveId")) {
    return {
      entityType: "career_move",
      entityId: sp.get("careerMoveId")!,
    };
  }
  // Prefer explicit pipeline / application / company before jobId (pipeline CTAs may include jobId).
  if (sp.get("pipelineId")) {
    return {
      entityType: "pipeline",
      entityId: sp.get("pipelineId")!,
      stage: sp.get("stage") ?? undefined,
    };
  }
  if (sp.get("applicationNumber")) {
    return {
      entityType: "application",
      entityId: sp.get("applicationNumber")!,
    };
  }
  if (sp.get("companySlug")) {
    return { entityType: "company", entityId: sp.get("companySlug")! };
  }
  if (sp.get("goalId")) {
    return { entityType: "goal", entityId: sp.get("goalId")! };
  }
  if (sp.get("planId")) {
    return { entityType: "plan", entityId: sp.get("planId")! };
  }
  if (sp.get("workId")) {
    return { entityType: "work", entityId: sp.get("workId")! };
  }
  if (sp.get("jobId")) {
    return { entityType: "job", entityId: sp.get("jobId")! };
  }
  return null;
}

function ActionConfirmCard({
  action,
  onConfirm,
  onDismiss,
}: {
  action: AssistantAction;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="my-2 flex max-w-xl flex-col gap-2 rounded-lg border border-border bg-surface/60 px-3 py-2.5">
      <p className="text-sm text-foreground">{actionLabel(action)}</p>
      <p className="text-xs text-muted-foreground">
        This may spend tokens or change your data. Confirm to continue.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          className="rounded-md bg-brand px-2.5 py-1 text-xs font-medium text-brand-foreground"
          onClick={onConfirm}
        >
          Confirm
        </button>
        <button
          type="button"
          className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function ActionLinkChip({
  action,
  onClick,
}: {
  action: AssistantAction;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface/60 px-2.5 py-1.5 text-xs font-medium text-brand transition hover:border-brand/40 hover:bg-brand-soft/20"
      onClick={onClick}
    >
      {actionLabel(action)}
    </button>
  );
}

export function AiAssistantPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const threadId = searchParams.get("thread");
  const promptHint = searchParams.get("prompt") ?? undefined;
  const entity = useMemo(() => entityFromParams(searchParams), [searchParams]);

  const [railCollapsed, setRailCollapsed] = useState(false);
  const [threadsSheetOpen, setThreadsSheetOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [running, setRunning] = useState(false);
  const [streamState, setStreamState] = useState<{
    text: string;
    tools: Array<{
      name: string;
      label?: string;
      args?: Record<string, unknown>;
      href?: string;
    }>;
    verb?: string;
    steps?: string[];
    durationMs?: number;
  } | null>(null);
  const [pendingActions, setPendingActions] = useState<
    Record<string, AssistantAction[]>
  >({});
  const [linkActions, setLinkActions] = useState<
    Record<string, AssistantAction[]>
  >({});
  const [entityTitle, setEntityTitle] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const resolvingEntityRef = useRef<string | null>(null);

  const threadsQuery = useQuery({
    queryKey: ["assistant-threads"],
    queryFn: listAssistantThreads,
    retry: false,
  });

  const entityHeaderQuery = useQuery({
    queryKey: [
      "assistant-entity-header",
      entity?.entityType,
      entity?.entityId,
      entityTitle,
    ],
    enabled: Boolean(entity),
    queryFn: async () => {
      if (!entity) return null;
      if (entity.entityType === "job") {
        const job = await getJob(entity.entityId);
        return {
          title: `${job.role} · ${job.company}`,
          subtitle: job.location || job.url,
          backHref: `/jobs/${entity.entityId}`,
          backLabel: "Back to job",
        };
      }
      if (entity.entityType === "company") {
        const company = await getCompany(entity.entityId);
        return {
          title: company.name,
          subtitle: company.global?.industry || company.slug,
          backHref: `/companies/${entity.entityId}`,
          backLabel: "Back to company",
        };
      }
      if (entity.entityType === "pipeline") {
        const detail = await getPipelineDetail(entity.entityId);
        return {
          title: detail.pipeline.name || detail.job?.role || "Pipeline",
          subtitle: entity.stage || detail.pipeline.currentStage,
          backHref: `/pipeline/${entity.entityId}`,
          backLabel: "Back to pipeline",
        };
      }
      if (entity.entityType === "career_move") {
        return {
          title: entityTitle || "Career move",
          subtitle: "Context from Moves",
          backHref: `/?move=${encodeURIComponent(entity.entityId)}`,
          backLabel: "Back to Moves",
        };
      }
      if (entity.entityType === "goal") {
        return {
          title: entityTitle || "Goal",
          subtitle: "Career goal",
          backHref: `/goals/${entity.entityId}`,
          backLabel: "Back to goal",
        };
      }
      if (entity.entityType === "plan") {
        return {
          title: entityTitle || "Plan",
          subtitle: "Career plan",
          backHref: `/plans/${entity.entityId}`,
          backLabel: "Back to plan",
        };
      }
      if (entity.entityType === "work") {
        return {
          title: entityTitle || "Work",
          subtitle: "Work item",
          backHref: `/work/${entity.entityId}`,
          backLabel: "Back to work",
        };
      }
      return {
        title: `Application #${entity.entityId}`,
        subtitle: undefined,
        backHref: `/reports/${entity.entityId}`,
        backLabel: "Back to report",
      };
    },
    retry: false,
  });

  useEffect(() => {
    if (!entity || threadId) return;
    const key = `${entity.entityType}:${entity.entityId}`;
    if (resolvingEntityRef.current === key) return;
    resolvingEntityRef.current = key;

    const resolve =
      entity.entityType === "career_move"
        ? resolveCommandItemThread({ careerMoveId: entity.entityId }).then(
            (res) => {
              if (res.taskTitle) setEntityTitle(res.taskTitle);
              return res;
            },
          )
        : resolveEntityThread({
            entityType: entity.entityType,
            entityId: entity.entityId,
            stage: entity.stage,
          }).then((res) => {
            if (res.taskTitle) setEntityTitle(res.taskTitle);
            return res;
          });

    void resolve
      .then(({ thread }) => {
        setSearchParams(
          (current) => {
            const next = new URLSearchParams(current);
            next.set("thread", thread.threadId);
            return next;
          },
          { replace: true },
        );
        void queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
      })
      .finally(() => {
        resolvingEntityRef.current = null;
      });
  }, [entity, threadId, queryClient, setSearchParams]);

  const loadThread = useCallback(async (id: string) => {
    const res = await getAssistantThread(id);
    setMessages(res.messages);
    setStreamState(null);
    // Re-scan assistant messages for confirmable + link actions
    const pending: Record<string, AssistantAction[]> = {};
    const links: Record<string, AssistantAction[]> = {};
    for (const m of res.messages) {
      if (m.role !== "assistant") continue;
      const text = m.blocks
        .filter((b) => b.type === "text")
        .map((b) => (b as { text: string }).text)
        .join("\n");
      const parsed = parseActionEnvelopes(text);
      const confirm = parsed.filter((a) => CONFIRM_ACTIONS.has(a.id));
      const link = parsed.filter((a) => LINK_ACTIONS.has(a.id));
      if (confirm.length) pending[m.messageId] = confirm;
      if (link.length) links[m.messageId] = link;
    }
    setPendingActions(pending);
    setLinkActions(links);
  }, []);

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      setStreamState(null);
      return;
    }
    void loadThread(threadId).catch(() => {
      setSearchParams({}, { replace: true });
    });
  }, [threadId, loadThread, setSearchParams]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, streamState]);

  async function executeAction(action: AssistantAction) {
    switch (action.id) {
      case "navigate": {
        const path = action.args.path;
        if (typeof path === "string" && path.startsWith("/")) {
          navigate(path);
        }
        break;
      }
      case "remember": {
        const fact = action.args.fact;
        if (typeof fact === "string" && fact.trim()) {
          const { memory } = await getAssistantMemory();
          const next = memory
            ? `${memory.trim()}\n- ${fact.trim()}`
            : `- ${fact.trim()}`;
          await putAssistantMemory(next);
        }
        break;
      }
      case "setProfile": {
        const patch: Record<string, unknown> = {};
        for (const key of [
          "name",
          "email",
          "location",
          "roles",
          "compMin",
          "compMax",
          "currency",
          "remote",
          "seniority",
        ] as const) {
          if (action.args[key] !== undefined) patch[key] = action.args[key];
        }
        if (Array.isArray(action.args.roles)) {
          patch.targetRoles = action.args.roles;
          delete patch.roles;
        }
        if (Object.keys(patch).length) {
          await updateProfile(patch as Parameters<typeof updateProfile>[0]);
        }
        break;
      }
      case "evaluate": {
        if (action.args.url) {
          await runEvaluation("evaluate", String(action.args.url), () => {});
        }
        break;
      }
      case "research": {
        if (action.args.target) {
          await runEvaluation("research", String(action.args.target), () => {});
        }
        break;
      }
      case "apply": {
        if (action.args.url) {
          navigate(`/apply?url=${encodeURIComponent(String(action.args.url))}`);
        }
        break;
      }
      case "filterPipeline": {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(action.args)) {
          if (v != null) params.set(k, String(v));
        }
        navigate(`/pipeline?${params.toString()}`);
        break;
      }
      case "evaluateCompany": {
        if (action.args.company) {
          navigate(
            `/explore?company=${encodeURIComponent(String(action.args.company))}`,
          );
        }
        break;
      }
      case "generatePdf": {
        if (action.args.n) {
          navigate(`/profile/ai-context?application=${encodeURIComponent(String(action.args.n))}`);
        } else {
          navigate("/profile/ai-context");
        }
        break;
      }
      case "setStatus": {
        if (action.args.n) {
          navigate(`/reports/${action.args.n}`);
        }
        break;
      }
      default:
        break;
    }
  }

  async function goNewChat() {
    abortRef.current?.abort();
    setMessages([]);
    setStreamState(null);
    setPendingActions({});
    try {
      const { thread } = await createAssistantThread({ title: "New chat" });
      setSearchParams({ thread: thread.threadId }, { replace: true });
      await queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
    } catch {
      setSearchParams({}, { replace: true });
    }
  }

  function selectThread(id: string) {
    setSearchParams({ thread: id }, { replace: true });
  }

  async function archiveThread(id: string) {
    const thread = threads.find((item) => item.threadId === id);
    const ok = window.confirm(
      `Archive “${thread?.title || "this chat"}”? It will be removed from the chat list.`,
    );
    if (!ok) return;
    await updateAssistantThread(id, { status: "archived" });
    await queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
    if (threadId === id) void goNewChat();
  }

  async function send(message: string, explicitThreadId?: string) {
    if (entity && !threadId && !explicitThreadId) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(true);
    setStreamState({ text: "", tools: [], verb: "Worked" });

    const activeThread = explicitThreadId ?? threadId ?? undefined;

    if (activeThread) {
      const optimistic: AssistantMessage = {
        userId: "",
        threadId: activeThread,
        messageId: `local-${Date.now()}`,
        seq: (messages[messages.length - 1]?.seq ?? 0) + 1,
        role: "user",
        blocks: [{ type: "text", text: message }],
        feedback: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
    }

    try {
      const meta = await sendAssistantMessage(
        {
          message,
          threadId: activeThread,
          ...(entityTitle ? { taskTitle: entityTitle } : {}),
          ...(entity?.stage ? { stage: entity.stage } : {}),
        },
        (event: StreamEvent, m) => {
          if (m.threadId && m.threadId !== threadId) {
            setSearchParams(
              (current) => {
                const next = new URLSearchParams(current);
                next.set("thread", m.threadId!);
                return next;
              },
              { replace: true },
            );
          }
          if (event.type === "text") {
            setStreamState((prev) => {
              const raw = (prev?.text ?? "") + event.text;
              const text = stripActionEnvelopes(
                raw
                  .replace(/^Thinking:\s*.+$/gim, "")
                  .trimStart(),
              );
              const steps = [
                ...(prev?.steps ?? []),
                ...[...raw.matchAll(/^Thinking:\s*(.+)$/gim)].map((mm) => mm[1]),
              ].filter((s, i, arr) => arr.indexOf(s) === i);
              return {
                text,
                tools: prev?.tools ?? [],
                verb: prev?.verb,
                steps: steps.length ? steps : prev?.steps,
                durationMs: prev?.durationMs,
              };
            });
          } else if (event.type === "tool") {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: [
                ...(prev?.tools ?? []),
                {
                  name: event.name,
                  label: event.label,
                  args: event.args,
                  href: event.href,
                },
              ],
              verb: prev?.verb,
              steps: prev?.steps,
              durationMs: prev?.durationMs,
            }));
          } else if (event.type === "status" && event.label.endsWith("…")) {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: prev?.tools ?? [],
              verb: event.label.replace(/…$/, ""),
              steps: prev?.steps,
              durationMs: prev?.durationMs,
            }));
          } else if (
            event.type === "status" &&
            event.label === "message_saved"
          ) {
            setStreamState((prev) => ({
              text: prev?.text ?? "",
              tools: prev?.tools ?? [],
              verb: m.verb ?? prev?.verb,
              steps: m.steps ?? prev?.steps,
              durationMs: m.durationMs ?? prev?.durationMs,
            }));
          }
        },
        ac.signal,
      );

      const finalId = meta.threadId ?? activeThread;
      if (finalId) {
        await loadThread(finalId);
        // Auto-run safe non-route actions (e.g. remember). Navigate/filter/apply stay as links.
        const res = await getAssistantThread(finalId);
        const lastAssistant = [...res.messages]
          .reverse()
          .find((m) => m.role === "assistant");
        if (lastAssistant) {
          const text = lastAssistant.blocks
            .filter((b) => b.type === "text")
            .map((b) => (b as { text: string }).text)
            .join("\n");
          const actions = parseActionEnvelopes(text);
          for (const action of actions) {
            if (
              !CONFIRM_ACTIONS.has(action.id) &&
              !LINK_ACTIONS.has(action.id)
            ) {
              void executeAction(action);
            }
          }
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["assistant-threads"] });
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        console.error(err);
      }
    } finally {
      setRunning(false);
      setStreamState(null);
    }
  }

  async function onPreset(preset: {
    id: string;
    name: string;
    text: string;
    autorun?: boolean;
  }) {
    if (entity && !threadId) return;
    if (!threadId) {
      const { thread } = await createAssistantThread({ title: preset.name });
      setSearchParams({ thread: thread.threadId }, { replace: true });
      await send(preset.text, thread.threadId);
      return;
    }
    await send(preset.text);
  }

  const threads = (threadsQuery.data?.threads ?? []).filter(
    (t) => t.status !== "archived",
  );
  const resolvingEntity = Boolean(entity && !threadId);
  const showEmpty = !threadId && messages.length === 0 && !streamState;
  const header = entityHeaderQuery.data;
  const starters = entity ? ENTITY_STARTERS[entity.entityType] : null;

  const actionCards = useMemo(() => {
    const cards: Record<string, ReactNode> = {};
    const messageIds = new Set([
      ...Object.keys(pendingActions),
      ...Object.keys(linkActions),
    ]);
    for (const messageId of messageIds) {
      const confirms = pendingActions[messageId] ?? [];
      const links = linkActions[messageId] ?? [];
      if (!confirms.length && !links.length) continue;
      cards[messageId] = (
        <div key={messageId} className="mt-2 space-y-2">
          {links.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {links.map((action, i) => (
                <ActionLinkChip
                  key={`link-${action.id}-${i}`}
                  action={action}
                  onClick={() => {
                    void executeAction(action);
                  }}
                />
              ))}
            </div>
          )}
          {confirms.map((action, i) => (
            <ActionConfirmCard
              key={`confirm-${action.id}-${i}`}
              action={action}
              onConfirm={() => {
                void executeAction(action).then(() => {
                  setPendingActions((prev) => {
                    const next = { ...prev };
                    const remaining = (next[messageId] ?? []).filter(
                      (_, idx) => idx !== i,
                    );
                    if (remaining.length) next[messageId] = remaining;
                    else delete next[messageId];
                    return next;
                  });
                });
              }}
              onDismiss={() => {
                setPendingActions((prev) => {
                  const next = { ...prev };
                  const remaining = (next[messageId] ?? []).filter(
                    (_, idx) => idx !== i,
                  );
                  if (remaining.length) next[messageId] = remaining;
                  else delete next[messageId];
                  return next;
                });
              }}
            />
          ))}
        </div>
      );
    }
    return cards;
  }, [pendingActions, linkActions]);

  return (
    <div className="flex h-[calc(100vh-0px)] min-h-0 overflow-hidden bg-background md:h-screen">
      <div className="hidden h-full md:block">
        <AssistantRail
          collapsed={railCollapsed}
          onToggleCollapse={() => setRailCollapsed((c) => !c)}
          threads={threads}
          activeThreadId={threadId}
          onNewChat={() => void goNewChat()}
          onSelectThread={selectThread}
          onArchiveThread={(id) => void archiveThread(id)}
        />
      </div>

      <MobileSheet
        open={threadsSheetOpen}
        onClose={() => setThreadsSheetOpen(false)}
        title="Threads"
        variant="full"
      >
        <AssistantRail
          embedded
          collapsed={false}
          onToggleCollapse={() => undefined}
          threads={threads}
          activeThreadId={threadId}
          onNewChat={() => {
            void goNewChat();
            setThreadsSheetOpen(false);
          }}
          onSelectThread={(id) => {
            selectThread(id);
            setThreadsSheetOpen(false);
          }}
          onArchiveThread={(id) => {
            void archiveThread(id);
            setThreadsSheetOpen(false);
          }}
        />
      </MobileSheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 md:hidden">
          <button
            type="button"
            onClick={() => setThreadsSheetOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-surface-hover px-2.5 py-1.5 text-xs font-medium text-foreground"
          >
            <MessageSquare className="size-3.5" />
            Threads
          </button>
          <button
            type="button"
            data-testid="assistant-new-chat"
            onClick={() => void goNewChat()}
            className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium"
          >
            New chat
          </button>
        </div>

        {entity ? (
          <div className="border-b border-border bg-surface/40 px-4 py-2.5">
            <Link
              to={header?.backHref ?? "/"}
              className="group flex max-w-full items-start gap-2 text-left"
            >
              <ArrowLeft className="mt-0.5 size-3.5 shrink-0 text-muted-foreground transition group-hover:text-foreground" />
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium text-foreground group-hover:underline">
                  {header?.backLabel ?? "Context"}
                  {header?.title ? ` · ${header.title}` : ""}
                </span>
                {header?.subtitle ? (
                  <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                    {header.subtitle}
                  </span>
                ) : null}
              </span>
            </Link>
            {starters ? (
              <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5">
                {starters.map((label) => (
                  <button
                    key={label}
                    type="button"
                    disabled={running || resolvingEntity}
                    onClick={() => void send(label)}
                    className="shrink-0 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium hover:bg-surface-hover disabled:opacity-50"
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          {showEmpty ? (
            <AssistantEmptyState
              onSend={(m) => void send(m)}
              onPreset={(p) => void onPreset(p)}
              running={running || resolvingEntity}
              initialPrompt={promptHint}
            />
          ) : (
            <AssistantThreadView
              threadId={threadId ?? "new"}
              messages={messages}
              streaming={streamState}
              onMessagesChange={setMessages}
              actionCards={actionCards}
            />
          )}
        </div>

        {!showEmpty && (
          <div className="border-t border-border px-4 py-3">
            <div className="mx-auto flex max-w-2xl flex-col items-center gap-2">
              <AssistantComposer
                placeholder="Reply to Assistant"
                disabled={running || resolvingEntity}
                onSend={(m) => void send(m)}
              />
              <p className="text-center text-[11px] text-muted-foreground">
                Chats may be shared with third parties, see{" "}
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => navigate("/settings/ai")}
                >
                  AI Policy
                </button>
                . Avoid sharing sensitive info.
              </p>
              {threadId && (
                <p className="font-mono text-[10px] text-faint">ID {threadId}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
